import React, { useState } from 'react';
import services from '../../services.json';
import { ServicesListWrapper } from './ServiceGrid.styles';
import { Box } from '@hiiretail/synergy-ui';

import { useTitle } from '../../hooks/useTitle';
import { ServiceCategory } from './service-category/ServiceCategory';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getTenantAlias } from '../../hooks/getTenantAlias';
import { filterServices } from './utils/filterServices';

const CATEGORY_STATES_KEY = 'hiiretail-category-states';

function loadCategoryStates(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(CATEGORY_STATES_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveCategoryStates(states: Record<string, boolean>) {
  try {
    localStorage.setItem(CATEGORY_STATES_KEY, JSON.stringify(states));
  } catch {
    // ignore storage errors
  }
}

export type ServicesItem = {
  name: string;
  description: string;
  route: string;
  external: boolean;
  beta: boolean;
  // if set, visibility of the tile will be determined by LD
  LDEnableFeatureFlag?: string;
  // optional tenants config, that allows disabling/enabling
  // the tile for specific tenant (access tenants alias as value)
  tenants?: { only?: string[]; ignore?: string[] };
};

export type ServicesCategory = {
  categoryId: string;
  category: string;
  items: ServicesItem[];
};

export const ServicesGrid: React.FC = () => {
  useTitle('Console');

  const flags = useFlags();
  const filteredServices = filterServices(services, getTenantAlias(), flags);

  const [categoryStates, setCategoryStates] = useState<Record<string, boolean>>(() =>
    loadCategoryStates(),
  );

  const handleToggle = (category: string, isOpen: boolean) => {
    const next = { ...categoryStates, [category]: isOpen };
    setCategoryStates(next);
    saveCategoryStates(next);
  };

  return (
    <ServicesListWrapper>
      <ServicesListWrapper>
        <Box pt={8} px={8} pb={12.5}>
          {filteredServices.map(({ categoryId, category, items }, index) => {
            const isExpanded = categoryId in categoryStates ? categoryStates[categoryId] : true;
            return (
              <Box key={categoryId} mb={3}>
                <ServiceCategory
                  category={category}
                  items={items}
                  index={index}
                  isExpanded={isExpanded}
                  onToggle={(isOpen) => handleToggle(categoryId, isOpen)}
                />
              </Box>
            );
          })}
        </Box>
      </ServicesListWrapper>
    </ServicesListWrapper>
  );
};
